import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { cefrApi, type DiagnosticExam } from '@/api/cefr';
import type { CEFRLevel } from '@/api/courses';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Select } from '@/components/ui/Select';
import { humanizeError } from '@/lib/errors';

const LEVELS: { value: CEFRLevel; label: string }[] = [
  { value: 'A1', label: 'A1 (Beginner)' },
  { value: 'A2', label: 'A2 (Elementary)' },
  { value: 'B1', label: 'B1 (Intermediate)' },
  { value: 'B2', label: 'B2 (Upper-Intermediate)' },
  { value: 'C1', label: 'C1 (Advanced)' },
];

/**
 * CEFR diagnostic exam — 5-step flow. Each step contains multiple-choice
 * questions; the backend grades on submit. ТЗ §5.5.
 */
export function StudentCEFRExamPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const active = useQuery({ queryKey: ['cefr', 'active'], queryFn: cefrApi.getActiveExam });

  const [targetLevel, setTargetLevel] = useState<CEFRLevel>('B1');
  const start = useMutation({
    mutationFn: () => cefrApi.startExam(targetLevel),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cefr', 'active'] }),
  });

  if (active.isLoading)
    return (
      <>
        <PageHeader title={t('cefr.title')} subtitle={t('cefr.subtitle')} />
        <Card variant="subtle" className="flex items-center justify-center gap-2 py-8 text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          {t('common.loading')}
        </Card>
      </>
    );

  return (
    <>
      <PageHeader title={t('cefr.title')} subtitle={t('cefr.subtitle')} />

      {active.isError && (
        <Card variant="subtle" className="mb-4">
          {t('cefr.loadError')}
        </Card>
      )}

      {active.data && active.data.status === 'in_progress' ? (
        <ExamRunner exam={active.data} onFinish={() => qc.invalidateQueries({ queryKey: ['cefr'] })} />
      ) : active.data && active.data.status === 'completed' ? (
        <Card>
          <CardTitle>{t('cefr.completed')}</CardTitle>
          <p className="mt-2">{t('cefr.yourLevel')}</p>
          <Pill variant="primary" className="mt-2 text-lg">{active.data.result_level}</Pill>
          <p className="mt-4 text-sm text-on-surface-variant">
            {t('cefr.scoreRetake', { score: Math.round(active.data.score ?? 0) })}
          </p>
          <Button className="mt-4" loading={start.isPending} onClick={() => start.mutate()}>
            {t('cefr.restart')}
          </Button>
          {start.isError && (
            <p className="mt-2 text-sm text-error">{humanizeError(start.error)}</p>
          )}
        </Card>
      ) : (
        <Card>
          <CardTitle>{t('cefr.startNew')}</CardTitle>
          <p className="mt-2 text-sm text-on-surface-variant">
            {t('cefr.startHint')}
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Select
              label={t('cefr.targetLevel')}
              options={LEVELS}
              value={targetLevel}
              onChange={(e) => setTargetLevel(e.target.value as CEFRLevel)}
            />
          </div>
          <Button className="mt-4" loading={start.isPending} onClick={() => start.mutate()}>
            {t('cefr.startBtn')}
          </Button>
          {start.isError && (
            <p className="mt-2 text-sm text-error">{humanizeError(start.error)}</p>
          )}
        </Card>
      )}
    </>
  );
}

function ExamRunner({ exam, onFinish }: { exam: DiagnosticExam; onFinish: () => void }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [step, setStep] = useState(exam.step);
  const submit = useMutation({
    // CEFR-001 fix: server now grades — we just hand it the answers.
    mutationFn: () => cefrApi.submitStep({ exam_id: exam.id, answers }),
    onSuccess: (next) => {
      if (next.status === 'completed') onFinish();
      else {
        setStep(next.step);
        setAnswers({});
      }
    },
  });

  // Real countdown to the exam's expiry (the backend sets expires_at, default
  // 30 минут). Falls back to no timer if the field is absent.
  const expiresMs = exam.expires_at ? new Date(exam.expires_at).getTime() : null;
  const [secondsLeft, setSecondsLeft] = useState(() =>
    expiresMs ? Math.max(0, Math.round((expiresMs - Date.now()) / 1000)) : null,
  );
  useEffect(() => {
    if (expiresMs == null) return;
    const tick = () => setSecondsLeft(Math.max(0, Math.round((expiresMs - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresMs]);

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <CardTitle>{t('cefr.step', { step, total: 5 })}</CardTitle>
        {secondsLeft != null && (
          <Pill variant={secondsLeft <= 60 ? 'danger' : 'warning'} title={t('cefr.timeLeft')}>
            <span className="material-symbols-outlined text-sm">timer</span>
            {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
          </Pill>
        )}
      </div>
      <ol className="space-y-4">
        {QUESTIONS_PER_STEP.map((q, idx) => (
          <li key={idx}>
            <p className="mb-2 font-semibold">{idx + 1}. {q.q}</p>
            <ul className="space-y-1">
              {q.options.map((o, oi) => (
                <li key={oi}>
                  <label className="flex items-center gap-2 rounded-button bg-surface-container-low p-2 text-sm hover:bg-surface-container">
                    <input
                      type="radio"
                      name={`q-${idx}`}
                      checked={answers[String(idx)] === oi}
                      onChange={() => setAnswers((a) => ({ ...a, [String(idx)]: oi }))}
                      className="accent-primary"
                    />
                    {o}
                  </label>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      <Button
        className="mt-6"
        fullWidth
        loading={submit.isPending}
        disabled={Object.keys(answers).length < QUESTIONS_PER_STEP.length}
        onClick={() => submit.mutate()}
      >
        {step >= 5 ? t('cefr.finish') : t('cefr.nextStep')}
      </Button>
      {submit.isError && (
        <p className="mt-3 text-center text-sm text-error">{humanizeError(submit.error)}</p>
      )}
    </Card>
  );
}

// Fallback question set. The backend grades the submitted choices against its
// own server-side answer key (keyed by step + question index), so the page only
// needs the prompts/options — never the correct answer. When a questions
// endpoint ships, these should be fetched per step instead of hardcoded.
const QUESTIONS_PER_STEP = [
  { q: 'She ___ to school every day.', options: ['go', 'goes', 'going'] },
  { q: 'How ___ books do you have?', options: ['many', 'much', 'lot'] },
  { q: 'I have ___ working here for 5 years.', options: ['been', 'was', 'be'] },
];
